/**
 * Hook to determine if the source recommendation banner should be shown
 * and provide actions for Apply, Customise, and Dismiss.
 *
 * Targets activated evidence users (>=5 queries this ISO week) who have no
 * existing source collections. "Closed" semantics (engagement, dismissal, or
 * impression limit) live in `src/lib/modalClosedRules.ts`; explicit actions
 * persist via `seen_modals` so they sync across devices.
 *
 * Analytics: emits `source_recommendation_{shown,dismissed,applied,customised}`
 * to Snowplow. The Iglu JSON schemas are the source of truth in the
 * `oscerai/snowplow` repo at `data-structures/com.heidi/source_recommendation_*.yaml`
 * (currently `2-0-0`; see oscerai/snowplow#125 for the `location` ->
 * `banner_location` rename). Amplitude is suppressed for these events
 * (`options: { amplitude: false }`) per ml-scribe AGENTS.md; Datadog RUM
 * still receives them. The `Applied` event is the only one with a
 * customise/diff payload — direct-Apply fires with empty diff arrays and
 * `is_customised: false`; the customise-wizard path fires with the diff
 * vs the offered recommendation and `is_customised: true`.
 */

import { floatingAskHeidiPhaseAtom } from '@/atoms/floatingAskHeidiAtoms';
import { seenModalIdsAtom } from '@/atoms/modalQueueAtoms';
import { sourceControlToggleAtom } from '@/atoms/sourceControlAtoms';
import { useIsSourceRecommendationBannerEnabled } from '@/features/flags';
import { IS_UNIFIED_COLLECTIONS_ENABLED } from '@/features/flags/legacy-flags';
import { useManualExposure } from '@/hooks/amplitude';
import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import { useQuerySourceRecommendations } from '@/hooks/query/useQuerySourceRecommendations';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useActivateRecommendedSources } from '@/hooks/useActivateRecommendedSources';
import { useDocumentCreationSuggestionBanner } from '@/hooks/useDocumentCreationSuggestionBanner';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { useTeamEvidenceEnforcement } from '@/hooks/useTeamEvidenceEnforcement';
import { track } from '@/lib/analytics/track';
import { isModalClosed } from '@/lib/modalClosedRules';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  SOURCE_RECOMMENDATION_DIFF_MAX_ITEMS,
  type SourceRecommendationBannerLocation,
} from '@/models/analytics/features/source-recommendation';
import { type Dispatch } from '@/store';
import { toast } from '@heidi/design-system/toast';
import {
  MAX_DOMAINS_PER_COLLECTION,
  MAX_DOMAINS_PER_SOURCE_LIST,
} from '@heidi/evidence/contracts';
import { useCollections, useSourceLists } from '@heidi/evidence/data';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { ModalId, ModalViewAction } from '@heidi/schema/ml-scribe';
import { countries } from '@heidi/utils/countries';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
interface UseSourceRecommendationBannerOptions {
  // When false, suppresses analytics + impression-counter side effects so
  // visibility-only callers (parents that gate other UI on shouldShow) don't
  // double-fire the events the banner component owns.
  trackImpressions?: boolean;
}

export function useSourceRecommendationBanner(
  location: SourceRecommendationBannerLocation = 'chat',
  { trackImpressions = true }: UseSourceRecommendationBannerOptions = {}
) {
  const intl = useIntl();
  const dispatch = useDispatch<Dispatch>();
  const isFlagEnabled = useIsSourceRecommendationBannerEnabled();
  const { isSessionlessEvidenceShown } = useEvidenceAccess();

  const sessionId = useCurrentSessionId();
  const { isShowing: showingDocumentCreationSuggestions } =
    useDocumentCreationSuggestionBanner(sessionId ?? undefined, {
      trackImpressions: false,
    });

  const { data: recommendationsData } = useQuerySourceRecommendations(isFlagEnabled);
  const sources = useMemo(
    () => recommendationsData?.sources ?? [],
    [recommendationsData?.sources]
  );
  const matchedCountryName = recommendationsData?.matched_country ?? '';
  const matchedCountryCode =
    countries.find((c) => c.name === matchedCountryName)?.countryCode ??
    matchedCountryName;
  // BE-matched specialty id (may differ from the user's profile specialty
  // when the BE falls back to GP because the user has no specialty_id).
  // Used only for analytics; display copy still uses `specialtyName` below.
  const matchedSpecialtyId = recommendationsData?.matched_specialty_id ?? null;

  const { sourceControl, webCollectionIdsChangeable } = useTeamEvidenceEnforcement();
  const hasSourcesEnforced =
    !sourceControl.webCollections.enabled || !webCollectionIdsChangeable;

  const seenModalIds = useAtomValue(seenModalIdsAtom);
  const setSeenModalIds = useSetAtom(seenModalIdsAtom);

  // Popover banner uses its own ModalId so dismissals are tracked separately
  const dismissModalId =
    location === 'popover'
      ? ModalId.SOURCE_RECOMMENDATION_APPLY_BANNER
      : ModalId.SOURCE_RECOMMENDATION_BANNER;
  const isDismissed = seenModalIds?.has(dismissModalId) ?? false;

  const sourceControlToggle = useAtomValue(sourceControlToggleAtom);
  const hasWebCollectionsApplied =
    sourceControlToggle.webCollections &&
    sourceControlToggle.selectedWebCollectionIds.length > 0;
  const setSourceControlToggle = useSetAtom(sourceControlToggleAtom);

  const setFloatingPhase = useSetAtom(floatingAskHeidiPhaseAtom);
  const { mutate: markModalAsViewed } = useMutateMarkModalAsViewed();

  const { data: userData } = useQueryUser((user) =>
    user
      ? {
          specialtyId: user.specialty_id,
          isEvidenceActivated: user.is_evidence_activated,
          modalView: user.seen_modals?.[dismissModalId],
        }
      : undefined
  );
  // Mirror useEvidenceSummarySuggestion: make the hydration gate explicit so
  // isExposureEligible / shouldShow don't evaluate until userData loads.
  const isUserHydrated = userData !== undefined;
  const isEvidenceActivated = userData?.isEvidenceActivated ?? false;
  const isClosedByConfig = isModalClosed(dismissModalId, userData?.modalView);

  const { activateRecommendedSources } = useActivateRecommendedSources();
  const [isApplying, setIsApplying] = useState(false);
  const { data: existingSourceLists } = useSourceLists();
  const { data: existingCollectionsData } = useCollections();

  // Both queries must hydrate before we can answer "has any collection?".
  // Treating `undefined` as "still loading" mirrors the original
  // `hasLoadedSourceLists` guard — it avoids flashing the banner during
  // the initial fetch, which would otherwise momentarily look like an
  // empty library for users who in fact have collections.
  //
  // `useSourceLists()` (GET /source-lists/) returns legacy SourceListDO
  // records PLUS unified collections projected from a legacy
  // SourceListDO via `_collection_to_source_list_do`. Pure unified
  // collections (created fresh via POST /evidence/collections, no
  // legacy_source_list_id) only show up in `useCollections()` (GET
  // /evidence/collections). Pre-fix, the banner only consulted the
  // legacy endpoint, so users with only pure unified collections saw
  // the banner — closes that gap and brings the visibility gate in
  // sync with the Apply path, which already considers both endpoints
  // (see `useActivateRecommendedSources`).
  const hasLoadedCollections =
    existingSourceLists !== undefined && existingCollectionsData !== undefined;
  const hasNoExistingCollections =
    hasLoadedCollections &&
    existingSourceLists.length === 0 &&
    existingCollectionsData.collections.length === 0;

  // Manual exposure for Amplitude experiment analysis (GRO-2753).
  // Eligibility mirrors shouldShow minus the flag gate so control-arm users
  // who match the targeting criteria are also counted.
  const isExposureEligible =
    isUserHydrated &&
    !!isSessionlessEvidenceShown &&
    isEvidenceActivated &&
    !hasSourcesEnforced &&
    !hasWebCollectionsApplied &&
    hasLoadedCollections &&
    hasNoExistingCollections &&
    !isClosedByConfig &&
    seenModalIds !== null &&
    !showingDocumentCreationSuggestions &&
    !isDismissed;
  useManualExposure({
    flagKey: 'release-evidence-enable-source-recommendation-banner',
    isEnabled: isFlagEnabled,
    isVisible: trackImpressions && isExposureEligible,
  });
  const { data: specialtiesData } = useQuerySpecialties();
  const specialtyName =
    specialtiesData?.specialties.find((s) => s.id === userData?.specialtyId)
      ?.localizedName ?? '';
  // Resolved name of the BE-matched specialty. Analytics-only (never
  // rendered), so it reads the canonical English `name` — logging
  // `localizedName` would fragment cross-locale aggregation of
  // `matched_specialty_name`. Falls back to '' during hydration;
  // downstream Snowplow schema treats empty as "unresolved".
  const matchedSpecialtyName =
    specialtiesData?.specialties.find((s) => s.id === matchedSpecialtyId)?.name ?? '';

  const shouldShow =
    isUserHydrated &&
    isFlagEnabled &&
    isSessionlessEvidenceShown &&
    isEvidenceActivated &&
    !hasSourcesEnforced &&
    !hasWebCollectionsApplied &&
    hasLoadedCollections &&
    hasNoExistingCollections &&
    sources.length > 0 &&
    !isClosedByConfig &&
    seenModalIds !== null &&
    !showingDocumentCreationSuggestions &&
    !isDismissed;

  // Fire once per mount. Increments view_count via the legacy/no-action
  // branch of mark_modal_as_viewed so `modalClosedRules` can auto-dismiss
  // after the configured impression limit without touching engage_count /
  // consecutive_dismiss_count. Skipped for visibility-only callers so the
  // banner component remains the single owner.
  const hasTrackedShown = useRef(false);
  useEffect(() => {
    if (!trackImpressions) return;
    if (shouldShow && !hasTrackedShown.current) {
      hasTrackedShown.current = true;
      markModalAsViewed({ modal_id: dismissModalId, increment_count: true });
      track({
        event_name: TrackingEventName.SourceRecommendationShown,
        props: {
          banner_location: location,
          sources_count: sources.length,
          matched_country: matchedCountryName,
          matched_specialty_id: matchedSpecialtyId,
          matched_specialty_name: matchedSpecialtyName,
        },
        // source_recommendation_* events are owned end-to-end by Snowplow
        // (Iglu schemas published via oscerai/snowplow#119 + #120, bumped
        // to 2-0-0 in #125 to rename `location` -> `banner_location` so
        // snowtype doesn't emit a `Location` alias that shadows the DOM
        // global). Amplitude fan-out is suppressed per ml-scribe AGENTS.md
        // ("do not send new events to Amplitude"); Datadog RUM still emits
        // — different surface, kept intentionally.
        options: { amplitude: false },
      });
    }
  }, [
    trackImpressions,
    shouldShow,
    location,
    sources.length,
    matchedCountryName,
    matchedSpecialtyId,
    matchedSpecialtyName,
    dispatch,
    markModalAsViewed,
    dismissModalId,
  ]);

  // Mark the banner as dismissed/engaged on the backend so it survives reloads.
  // The optimistic atom add keeps the banner hidden immediately within the
  // current session; the backend action increments the persistent counter
  // (consecutive_dismiss_count / engage_count) that `useQueryUser` reads on
  // refetch to re-hydrate the atom.
  const markSeen = useCallback(
    (action: ModalViewAction) => {
      setSeenModalIds((prev) => new Set([...(prev ?? []), dismissModalId]));
      markModalAsViewed({
        modal_id: dismissModalId,
        increment_count: true,
        action,
      });
    },
    [setSeenModalIds, markModalAsViewed, dismissModalId]
  );

  const dismiss = useCallback(() => {
    markSeen(ModalViewAction.dismiss);
    track({
      event_name: TrackingEventName.SourceRecommendationDismissed,
      props: {
        banner_location: location,
        sources_count: sources.length,
        matched_country: matchedCountryName,
        matched_specialty_id: matchedSpecialtyId,
        matched_specialty_name: matchedSpecialtyName,
      },
      options: { amplitude: false },
    });
  }, [
    markSeen,
    location,
    sources.length,
    matchedCountryName,
    matchedSpecialtyId,
    matchedSpecialtyName,
  ]);

  /** Activate a collection in source control as a single atomic update. */
  const activateCollection = useCallback(
    (collectionId: string) => {
      setSourceControlToggle((prev) => ({
        ...prev,
        webCollections: true,
        webCollectionsMode: 'collections' as const,
        selectedWebCollectionIds: [collectionId],
      }));
    },
    [setSourceControlToggle]
  );

  const recommendedDomains = sources.map((s) => s.domain);
  const safeCountryCode = matchedCountryCode.trim();
  const defaultCollectionName =
    specialtyName && safeCountryCode
      ? intl.formatMessage(
          { defaultMessage: '{specialty} - {countryCode}', id: 'qu5ct86XXl' },
          { specialty: specialtyName, countryCode: safeCountryCode }
        )
      : specialtyName || safeCountryCode;

  const onApply = useCallback(async () => {
    setIsApplying(true);
    // Snap the unified-collections flag once at the top of the handler so the
    // analytics-reported cap and the cap that `useActivateRecommendedSources`
    // uses to slice the persisted domains stay in lockstep, even if Flagsmith
    // happens to flip the flag while the activate promise is in flight.
    const isUnified = IS_UNIFIED_COLLECTIONS_ENABLED();
    const maxDomains = isUnified
      ? MAX_DOMAINS_PER_COLLECTION
      : MAX_DOMAINS_PER_SOURCE_LIST;
    let collectionId: string | null = null;
    try {
      collectionId = await activateRecommendedSources();
    } finally {
      setIsApplying(false);
    }
    if (!collectionId) return;
    toast({
      // This id predates the hash convention and is already translated in all
      // 20 locale catalogues, which key by the bare id. Re-hashing it to the
      // value the rule wants orphans every one of those translations.
      // eslint-disable-next-line formatjs/enforce-id
      title: intl.formatMessage(
        { defaultMessage: 'Sources added to {name}', id: 'qR3vMnX8Yt' },
        { name: defaultCollectionName }
      ),
    });
    // Focus the floating evidence input so the user sees their sources are active
    setFloatingPhase('focused');
    // Direct-Apply fires the Applied event with empty diff arrays and
    // `is_customised: false`. `total_domains_in_collection` reflects the
    // post-cap count (mirrors the slice in `useActivateRecommendedSources`).
    // The activate hook's "find existing" branch is guarded by the
    // `hasNoExistingCollections` visibility gate, so this math is exact
    // for every banner-driven activation.
    const cappedCount = Math.min(sources.length, maxDomains);
    track({
      event_name: TrackingEventName.SourceRecommendationApplied,
      props: {
        banner_location: location,
        sources_count: sources.length,
        total_domains_in_collection: cappedCount,
        matched_country: matchedCountryName,
        matched_specialty_id: matchedSpecialtyId,
        matched_specialty_name: matchedSpecialtyName,
        is_customised: false,
        removed_domains: [],
        removed_domain_count: 0,
        added_domains: [],
        added_domain_count: 0,
      },
      options: { amplitude: false },
    });
    markSeen(ModalViewAction.engage);
  }, [
    activateRecommendedSources,
    defaultCollectionName,
    intl,
    setFloatingPhase,
    location,
    sources.length,
    matchedCountryName,
    matchedSpecialtyId,
    matchedSpecialtyName,
    markSeen,
  ]);

  // Customise: open the create collection wizard pre-populated with recommended domains
  const [isCustomiseOpen, setIsCustomiseOpen] = useState(false);

  const onCustomise = useCallback(() => {
    setIsCustomiseOpen(true);
    track({
      event_name: TrackingEventName.SourceRecommendationCustomised,
      props: {
        banner_location: location,
        sources_count: sources.length,
        matched_country: matchedCountryName,
        matched_specialty_id: matchedSpecialtyId,
        matched_specialty_name: matchedSpecialtyName,
      },
      options: { amplitude: false },
    });
  }, [
    location,
    sources.length,
    matchedCountryName,
    matchedSpecialtyId,
    matchedSpecialtyName,
  ]);

  const onCustomiseComplete = useCallback(
    (collection: {
      id: string;
      type: 'documents' | 'web';
      domainCount: number;
      savedDomains: string[];
    }) => {
      activateCollection(collection.id);
      // Customise diff: which recommended domains the clinician removed,
      // and which net-new domains they added. Feeds the down-weight /
      // gap-fill curation signal back into find_medical_web_domains.
      // For `type: 'documents'` the user staged document uploads instead
      // of a web list — `savedDomains` is empty so the diff degrades to
      // "everything offered was removed, nothing added", which is what
      // the curation pipeline expects for that case.
      const savedSet = new Set(collection.savedDomains);
      const offeredSet = new Set(recommendedDomains);
      // Defensive clamp at the Iglu schema's `maxItems` ceiling so the
      // event never silently drops if either the BE cap on persisted
      // domains or the schema ceiling drifts in the future. The wizard
      // already caps `savedDomains` at `MAX_DOMAINS_PER_COLLECTION = 300`,
      // so this clamp is a no-op today (300 < 1000) — it's purely a
      // safety net. `removed_domain_count` / `added_domain_count` track
      // the clamped array length, per the schema's "Length of {array}"
      // definition.
      const removed = recommendedDomains
        .filter((d) => !savedSet.has(d))
        .slice(0, SOURCE_RECOMMENDATION_DIFF_MAX_ITEMS);
      const added = collection.savedDomains
        .filter((d) => !offeredSet.has(d))
        .slice(0, SOURCE_RECOMMENDATION_DIFF_MAX_ITEMS);
      track({
        event_name: TrackingEventName.SourceRecommendationApplied,
        props: {
          banner_location: location,
          sources_count: sources.length,
          total_domains_in_collection: collection.domainCount,
          matched_country: matchedCountryName,
          matched_specialty_id: matchedSpecialtyId,
          matched_specialty_name: matchedSpecialtyName,
          is_customised: true,
          removed_domains: removed,
          removed_domain_count: removed.length,
          added_domains: added,
          added_domain_count: added.length,
        },
        options: { amplitude: false },
      });
      markSeen(ModalViewAction.engage);
    },
    [
      activateCollection,
      location,
      sources.length,
      matchedCountryName,
      matchedSpecialtyId,
      matchedSpecialtyName,
      recommendedDomains,
      markSeen,
    ]
  );

  const onDismiss = dismiss;

  return {
    shouldShow,
    sources,
    matchedCountry: matchedCountryName,
    matchedCountryCode,
    specialtyName,
    isApplying,
    onApply,
    onCustomise,
    onDismiss,
    // Customise modal state
    isCustomiseOpen,
    setIsCustomiseOpen,
    onCustomiseComplete,
    recommendedDomains,
    defaultCollectionName,
  };
}
